import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Sign in failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-brand-navy">
      {/* Left - brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage:
               "url(https://images.unsplash.com/photo-1687197180710-b2b9484a3c5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjB3YXZlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODM4ODY3ODd8MA&ixlib=rb-4.1.0&q=85)",
               backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/90 via-brand-navy/70 to-brand-aqua/30" />
        <div className="relative flex items-center gap-3">
          <img src="/assets/figuraid-logo.png" alt="Figuraid" className="w-12 h-12 object-contain bg-white/95 rounded-md p-1" />
          <div>
            <div className="font-heading text-xl font-semibold">FiguraidOS</div>
            <div className="text-xs text-slate-300">Internal Operations Platform</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight max-w-md">
            One platform.<br />Three business units.<br />
            <span className="text-brand-aqua">Zero friction.</span>
          </h1>
          <p className="text-slate-300 mt-4 max-w-md">
            Unified operations for FigureWeb, FigureCX, and Telecom — CRM, tickets, invoicing, HR, and more.
          </p>
        </div>
        <div className="relative text-xs text-slate-400">
          © 2026 Figuraid Ltd  ·  72/73 Ralph Shodeinde Street, Central Business District, Abuja
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">Welcome back — access your FiguraidOS workspace.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Work email</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-white" data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-white" data-testid="login-password"
              />
            </div>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

          <Button type="submit" disabled={busy}
                  className="w-full bg-brand-aqua hover:bg-brand-aqua-hover text-white h-11 text-sm font-medium"
                  data-testid="login-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {busy ? "Signing in..." : "Sign in to FiguraidOS"}
          </Button>

          <div className="text-center">
            <a href="/forgot-password" className="text-xs text-slate-500 hover:text-brand-aqua-hover" data-testid="forgot-password-link">
              Forgot your password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
